import { Member } from '@wix/ambassador-members-ng-api/types';
import { DateTimeFormatter } from '@wix/bookings-date-time';
import { OfferedAsType } from '@wix/bookings-uou-types';
import { useSettings } from '@wix/tpa-settings/react';
import { useExperiments, useTranslation } from '@wix/yoshi-flow-editor';
import sortBy from 'lodash/sortBy';
import React, { useMemo } from 'react';
import { ExperimentsConsts } from '../../../../../consts/experiments';
import { DynamicPriceErrorType, FormError } from '../../../../../types/errors';
import { FormStatus } from '../../../../../types/form-state';
import {
  PaymentMethod,
  PaymentOption,
  PaymentType,
  ReservedPaymentOptionIds,
} from '../../../../../types/types';
import { getServiceSlotIdentifier, mapToArray } from '../../../../../utils';
import { getErrorByType } from '../../../../../utils/errors/errors';
import {
  getDefaultPaymentOptionId,
  getPaymentTypeFromOption,
  getSlotServicePaymentOptions,
  sortPaymentOptions,
} from '../../../../../utils/payment';
import {
  ServiceData,
  SlotService,
  SlotServices,
} from '../../../../../utils/state/types';
import { getDefaultPaymentType } from '../../../../../utils/state/utils/utils';
import { useFormActions } from '../../../Hooks/useFormActions';
import {
  renderSlotDynamicPricesDropdown,
  shouldRenderDynamicPriceDropdown,
} from '../../PriceOptionDropdownContainer/PriceOptionDropdownContainer';
import { MultiPaymentSelectionContainerDataHooks } from './datahooks';
import { ServicePaymentDropdown } from './ServicePaymentDropdown/ServicePaymentDropdown';

export type MultiPaymentSelectionContainerProps = {
  serviceData: ServiceData;
  memberDetails?: Member;
  numberOfParticipants: number;
  dateRegionalSettingsLocale: string;
  isPricingPlanInstalled: boolean;
  status: FormStatus;
  errors: FormError[];
  isCart: boolean | undefined;
};

export const getMultiPaymentSelectionContainer = (
  props: MultiPaymentSelectionContainerProps,
) => {
  const paymentSelection = RenderMultiPaymentSelection(props);

  if (!paymentSelection) {
    return null;
  }

  return <>{paymentSelection}</>;
};

export const MultiPaymentSelectionContainer: React.FC<
  MultiPaymentSelectionContainerProps
> = (props) => {
  const paymentSelection = RenderMultiPaymentSelection(props);

  if (!paymentSelection) {
    return null;
  }

  return <>{paymentSelection}</>;
};

const RenderMultiPaymentSelection = ({
  serviceData,
  isPricingPlanInstalled,
  numberOfParticipants,
  dateRegionalSettingsLocale,
  memberDetails,
  status,
  errors,
  isCart,
}: MultiPaymentSelectionContainerProps) => {
  const { setDefaultPaymentOptions, onSelectedVariants, clearErrorByTypes } =
    useFormActions();
  const { t } = useTranslation();
  const { experiments } = useExperiments();
  const settings = useSettings();
  const dateAndTimeFormatter = useMemo(
    () => new DateTimeFormatter(dateRegionalSettingsLocale),
    [dateRegionalSettingsLocale],
  );

  const isUseNewBenefitsAPIUoUEnabled = experiments.enabled(ExperimentsConsts.UseNewBenefitsAPIUoU);
  const slotsList = mapToArray<SlotService>(serviceData.slotServices);
  const sortOrder = getDefaultPaymentType(
    settings,
    mapToArray<SlotService>(serviceData.slotServices)[0].service.paymentTypes,
  );
  const lineItemIds = slotsList
    .map((slotService) => slotService.nestedSlot.lineItemId)
    .join(',');
  const servicesPaymentOptionsMap: { [key: string]: PaymentOption[] } =
    useMemo(() => {
      const result: { [key: string]: PaymentOption[] } = {};
      const objectToUpdate: {
        [key: string]: {
          selectedPaymentOption: PaymentOption;
          selectedPaymentType: PaymentMethod;
        };
      } = {};
      slotsList.forEach((service) => {
        result[getServiceSlotIdentifier(service.nestedSlot)] =
          sortPaymentOptions(
            getSlotServicePaymentOptions({
              slotService: service,
              isPricingPlanInstalled,
              numberOfParticipants,
              t,
              dateAndTimeFormatter,
              dateRegionalSettingsLocale,
              experiments,
              isCart,
            }),
            sortOrder,
            isUseNewBenefitsAPIUoUEnabled,
          );

        const newDefaultId = getDefaultPaymentOptionId({
          servicePayment: service.service.payment,
          isPricingPlanInstalled,
          settings,
          memberships: service.memberships,
          isDynamicPricingCustomOptions:
            !!service.dynamicPriceInfo?.customOptions,
          hasEligiblePlans: Boolean(
            service.possiblePlans?.length || service.eligiblePlans?.length,
          ),
          experiments,
          paymentTypes: service.service.paymentTypes,
        });

        if (newDefaultId) {
          let newPaymentOption;
          if (newDefaultId === ReservedPaymentOptionIds.BuyAPricingPlan) {
            newPaymentOption = result[
              getServiceSlotIdentifier(service.nestedSlot)
            ].find((option) => option.type === PaymentType.BUY_PLAN);
          } else {
            newPaymentOption = result[
              getServiceSlotIdentifier(service.nestedSlot)
            ].find((option) => option.id === newDefaultId);
          }
          if (newPaymentOption) {
            const newPaymentType = getPaymentTypeFromOption(newPaymentOption);
            objectToUpdate[getServiceSlotIdentifier(service.nestedSlot)] = {
              selectedPaymentOption: newPaymentOption,
              selectedPaymentType: newPaymentType!,
            };
          }
        }
      });

      setDefaultPaymentOptions(objectToUpdate);

      return result;
    }, [lineItemIds, memberDetails?.id, dateRegionalSettingsLocale, sortOrder]);
  const getServiceLabel = (service: SlotService) => {
    if (
      slotsList.filter((s) => s.service.name === service.service.name)
        .length === 1
    ) {
      return t('app.bookings-form.payment-section.service-dropdown.label', {
        serviceName: service.service.name,
      });
    } else {
      return t(
        'app.bookings-form.payment-section.service-dropdown.label-at-time',
        {
          serviceName: service.service.name,
          serviceTime: dateAndTimeFormatter.formatTime(
            service.nestedSlot.startDate,
          ),
        },
      );
    }
  };

  const firstDynamicPriceError = getErrorByType({
    errorType: DynamicPriceErrorType,
    errors,
  });

  const isServiceWithPricingPlan = (slotService: SlotService) => {
    return slotService.service.payment.offeredAs.includes(
      OfferedAsType.PRICING_PLAN,
    );
  };

  const isAllServicesHaveOnePaymentOption = () => {
    return slotsList.every((slotService) => {
      return (
        servicesPaymentOptionsMap[
          getServiceSlotIdentifier(slotService.nestedSlot)
        ].length === 1
      );
    });
  };

  const shouldRenderPaymentDropdown = (slotService: SlotService): boolean => {
    const casesNotToShow =
      ((slotService.paymentDetails.isFree ||
        slotService.paymentDetails.price === 0) &&
        !isServiceWithPricingPlan(slotService)) ||
      servicesPaymentOptionsMap[
        getServiceSlotIdentifier(slotService.nestedSlot)
      ].length === 1;

    return !casesNotToShow;
  };

  const shouldRenderSlotPayment = (slotService: SlotService) => {
    return (
      shouldRenderPaymentDropdown(slotService) ||
      shouldRenderDynamicPriceDropdown(slotService)
    );
  };
  const isAnyServiceValidToView = () => {
    return slotsList.some((s) => shouldRenderSlotPayment(s));
  };
  const sortPaymentOptionsByStartTime = (slotServices: SlotServices) => {
    return sortBy(
      Object.entries(slotServices),
      (y) => y[1].nestedSlot.startDate,
    );
  };

  if (slotsList.length === 1) {
    const slotService = slotsList[0];

    if (
      !shouldRenderPaymentDropdown(slotService) &&
      !shouldRenderDynamicPriceDropdown(slotService)
    ) {
      return null;
    }

    return (
      <div data-hook={MultiPaymentSelectionContainerDataHooks.SINGLE}>
        {shouldRenderPaymentDropdown(slotService) && (
          <ServicePaymentDropdown
            service={slotService}
            paymentOptions={
              servicesPaymentOptionsMap[
              getServiceSlotIdentifier(slotService.nestedSlot)
              ]
            }
            label={t('app.bookings-form.payment-options-label', {
              serviceName: getServiceLabel(slotService),
            })}
            key={getServiceSlotIdentifier(slotService.nestedSlot)}
          />
        )}
        {renderSlotDynamicPricesDropdown(
          t,
          dateAndTimeFormatter,
          slotService,
          onSelectedVariants,
          clearErrorByTypes,
          serviceData,
          status,
          firstDynamicPriceError,
        )}
      </div>
    );
  }

  const forceRenderPaymentDropdowns = !isAllServicesHaveOnePaymentOption();

  if (
    !forceRenderPaymentDropdowns ||
    !isAnyServiceValidToView() ||
    !servicesPaymentOptionsMap
  ) {
    return null;
  }

  return (
    <div data-hook={MultiPaymentSelectionContainerDataHooks.MAIN}>
      {sortPaymentOptionsByStartTime(serviceData.slotServices).map(
        ([slotIdentifier, slotService]) => {
          return (
            <>
              {(forceRenderPaymentDropdowns ||
                shouldRenderPaymentDropdown(slotService)) && (
                  <ServicePaymentDropdown
                    service={slotService}
                    paymentOptions={servicesPaymentOptionsMap[slotIdentifier]}
                    label={t('app.bookings-form.payment-options-label', {
                      serviceName: getServiceLabel(slotService),
                    })}
                    key={getServiceSlotIdentifier(slotService.nestedSlot)}
                  />
                )}
              {renderSlotDynamicPricesDropdown(
                t,
                dateAndTimeFormatter,
                slotService,
                onSelectedVariants,
                clearErrorByTypes,
                serviceData,
                status,
                firstDynamicPriceError,
              )}
            </>
          );
        },
      )}
    </div>
  );
};
